import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { CWP_IT_MULTI_RECEIVING } from '@shared/helpers/feature-toggles';
import { wldOrLegacyDocUploadResolver } from '@shared/mfe-resolver/wld-or-legacy-doc-upload.resolver';

import { AuthGuard } from './core/guards/auth/auth.guard';
import { ClientDataGuard } from './core/guards/client/client-data.guard';
import { GatekeeperGuard } from './core/guards/gatekeeper/gatekeeper.guard';
import { IncapacitatedGuard } from './core/guards/incapacitated/incapacitated.guard';
import { InitStateGuard } from './core/guards/init-state/init-state.guard';

const multiITRouteData = {
  gatekeeperToggle: CWP_IT_MULTI_RECEIVING,
  redirectLocationWhenToggleOff: 'internal-transfer/overview',
};

export const internalTransferRoutes: Routes = [
  {
    path: 'internal-transfer',
    children: [
      {
        path: '',
        redirectTo: 'overview',
        pathMatch: 'full',
      },
      {
        path: 'overview',
        loadChildren: () =>
          import('./public/internal-transfer-overview/internal-transfer-overview.module').then(
            (m) => m.InternalTransferOverviewModule,
          ),
      },
      {
        path: 'triage',
        loadChildren: () =>
          import('./public/internal-transfer-triage/internal-transfer-triage.module').then(
            (m) => m.InternalTransferTriageModule,
          ),
      },
      {
        path: 'summary',
        loadChildren: () =>
          import('./public/internal-transfer-summary/internal-transfer-summary.module').then(
            (m) => m.InternalTransferSummaryModule,
          ),
      },
      {
        path: 'multi-triage',
        canActivate: [GatekeeperGuard],
        data: multiITRouteData,
        loadChildren: () =>
          import(
            './public/internal-transfers/triage-multi/internal-transfer-triage-multi.module'
          ).then((m) => m.InternalTransferTriageMultiModule),
      },
      {
        path: 'multi-summary',
        canActivate: [GatekeeperGuard],
        data: multiITRouteData,
        loadChildren: () =>
          import(
            './public/internal-transfers/summary-multi/internal-transfer-summary-multi.module'
          ).then((m) => m.InternalTransferSummaryMultiModule),
      },
    ],
  },
  {
    path: 'secure/internal-transfer',
    canActivate: [AuthGuard, ClientDataGuard, InitStateGuard, IncapacitatedGuard],
    children: [
      {
        path: 'contact-info',
        loadChildren: () =>
          import('./secure/contact-info/contact-info.module').then((m) => m.ContactInfoModule),
      },
      {
        path: 'cost-basis-election',
        loadChildren: () =>
          import(
            './secure/internal-transfer-cost-basis-election/internal-transfer-cost-basis-election.module'
          ).then((m) => m.InternalTransferCostBasisElectionModule),
      },
      {
        path: 'employment',
        loadChildren: () =>
          import('./secure/employment/employment.module').then((m) => m.EmploymentModule),
      },
      {
        path: 'upload',
        resolve: {
          'doc-upload-mfe': wldOrLegacyDocUploadResolver,
        },
        loadChildren: () =>
          import('./secure/document-upload/document-upload.module').then(
            (m) => m.DocumentUploadModule,
          ),
      },
      {
        path: 'expired',
        loadChildren: () =>
          import('./secure/internal-transfer-expired/internal-transfer-expired.module').then(
            (m) => m.InternalTransferExpiredModule,
          ),
      },
      {
        path: 'holdings-review',
        loadChildren: () =>
          import(
            './secure/internal-transfer-holdings-review/containers/internal-transfer-holdings-review-classic/internal-transfer-holdings-review-classic.module'
          ).then((m) => m.InternalTransferHoldingsReviewClassicModule),
      },
      {
        path: 'holdings-review-multisigner',
        loadChildren: () =>
          import(
            './secure/internal-transfer-holdings-review/containers/internal-transfer-holdings-review-simple/internal-transfer-holdings-review-simple.module'
          ).then((m) => m.InternalTransferHoldingsReviewSimpleModule),
      },
      {
        path: 'personal-info',
        loadChildren: () =>
          import('./secure/personal-info/personal-info.module').then((m) => m.PersonalInfoModule),
      },
      {
        path: 'settlement-fund',
        loadChildren: () =>
          import('./secure/settlement-fund/settlement-fund.module').then(
            (m) => m.SettlementFundModule,
          ),
      },
      {
        path: 'new-account-review',
        loadChildren: () =>
          import('./secure/new-account-review/new-account-review.module').then(
            (m) => m.NewAccountReviewModule,
          ),
      },
      {
        path: 'terms',
        loadChildren: () => import('./secure/terms/terms.module').then((m) => m.TermsModule),
      },
      {
        path: 'transfer-details',
        loadChildren: () =>
          import('./secure/internal-transfer-details/internal-transfer-details.module').then(
            (m) => m.InternalTransferDetailsModule,
          ),
      },
      {
        path: 'multi-from-to',
        canActivate: [GatekeeperGuard],
        data: multiITRouteData,
        loadChildren: () =>
          import('./secure/internal-transfers/from-to/internal-transfer-from-to.module').then(
            (m) => m.InternalTransferFromToModule,
          ),
      },
      {
        path: 'multi-amounts',
        canActivate: [GatekeeperGuard],
        data: multiITRouteData,
        loadChildren: () =>
          import('./secure/internal-transfers/amounts/internal-transfer-amounts.module').then(
            (m) => m.InternalTransferAmountsModule,
          ),
      },
      {
        path: 'multi-cost-basis-election',
        canActivate: [GatekeeperGuard],
        data: multiITRouteData,
        loadChildren: () =>
          import(
            './secure/internal-transfers/cost-basis-election-multi/internal-transfer-cost-basis-election-multi.module'
          ).then((m) => m.InternalTransferCostBasisElectionMultiModule),
      },
      {
        path: 'multi-review',
        canActivate: [GatekeeperGuard],
        data: multiITRouteData,
        loadChildren: () =>
          import(
            './secure/internal-transfers/multi-review/internal-transfer-multi-review.module'
          ).then((m) => m.InternalTransferMultiReviewModule),
      },
      {
        path: 'multi-review-multisigner',
        loadChildren: () =>
          import(
            './secure/internal-transfers/multi-review/internal-transfer-multi-review-multisigner.module'
          ).then((m) => m.InternalTransferMultiReviewMultisignerModule),
      },
      {
        path: '',
        loadChildren: () =>
          import('./secure/client-due-diligence/client-due-diligence.module').then(
            (m) => m.ClientDueDiligenceModule,
          ),
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(internalTransferRoutes)],
  exports: [RouterModule],
  providers: [GatekeeperGuard],
})
export class InternalTransferRoutingModule {}
